import { useEffect, useRef, useState } from "react";
import { cn } from "@/utils/cn";

interface AnimatedStatValueProps {
  value: string;
  className?: string;
}

const NUMERIC_PATTERN = /^\d+$/;

/**
 * Affiche une valeur statistique.
 * - Valeur purement numérique : compteur qui démarre à l'entrée dans le viewport.
 * - Autres formats (ex. "100%", "24/7") : apparition élégante (fondu + montée + zoom léger).
 * Le rendu et le style sont identiques à un simple paragraphe de valeur.
 */
export default function AnimatedStatValue({ value, className }: AnimatedStatValueProps) {
  const isNumeric = NUMERIC_PATTERN.test(value.trim());
  const target = isNumeric ? parseInt(value.trim(), 10) : 0;
  const ref = useRef<HTMLParagraphElement>(null);
  const [visible, setVisible] = useState(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isNumeric || !visible) return;

    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(target);
      return;
    }

    let raf = 0;
    const duration = 1500;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isNumeric, visible, target]);

  if (isNumeric) {
    return (
      <p ref={ref} className={className}>
        <span className="inline-block tabular-nums">{display}</span>
      </p>
    );
  }

  return (
    <p ref={ref} className={className}>
      <span
        className={cn(
          "inline-block transition-all duration-700 ease-out",
          visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-1.5 scale-90"
        )}
      >
        {value}
      </span>
    </p>
  );
}